import type { ImageModelId } from "@fitness-app/data-models/ai/models";
import { getImageModelSelectOptions, imageModelTasks } from "@fitness-app/data-models/ai/tasks";
import { useState } from "react";
import { DeleteOutlined, ExperimentOutlined, PictureOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button, Image, Input, message, Popover, Select, Skeleton, Space, Typography, Upload } from "antd";
import { type RcFile } from "antd/es/upload";

import { getErrorMessage } from "@fitness-app/utils";

import { analyticsService, trainersApi } from "~/store/initializeStore";

// TRE-1583 — AI cover generator for challenges, modeled on DishAiGenerator, plus a reference-image
// input: an uploaded photo is passed to the model as a native image input (style/composition
// reference — nano banana accepts it directly, OpenAI goes through images.edit server-side).

export type ChallengeCoverContext = {
  name: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

type ReferenceImage = {
  data: string;
  mediaType: string;
  fileName: string;
};

type CoverModel = ImageModelId;

const MAX_REFERENCE_MB = 8;

const fileToBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Nie udało się odczytać pliku"));
    reader.readAsDataURL(file);
  });

const useChallengeCoverGenerator = () => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async ({
      challengeId,
      prompt,
      model,
      challengeContext,
      referenceImage,
    }: {
      challengeId: string;
      prompt: string;
      model?: CoverModel;
      challengeContext: ChallengeCoverContext;
      referenceImage?: ReferenceImage | null;
    }) => {
      setImageUrl(null);
      const response = await trainersApi.post<{ url: string }>("/generate-challenge-cover-image", {
        challengeId,
        prompt,
        model,
        challengeContext,
        referenceImage: referenceImage ? { data: referenceImage.data, mediaType: referenceImage.mediaType } : null,
      });
      setImageUrl(response.data.url);
    },
    onError: (error) => {
      message.error(getErrorMessage(error));
    },
    onSuccess: (_data, variables) => {
      // No raw prompt in analytics — it can contain client details; length is enough signal.
      analyticsService.track("generate_challenge_cover", {
        promptLength: variables.prompt.length,
        model: variables.model,
        withReference: Boolean(variables.referenceImage),
      });
    },
  });

  return {
    mutation,
    isLoading: mutation.isLoading,
    imageUrl,
  };
};

export const ChallengeCoverAiGenerator = ({
  challengeId,
  challengeContext,
  setCoverUrl,
}: {
  challengeId: string;
  challengeContext: ChallengeCoverContext;
  setCoverUrl: (url: string) => void;
}) => {
  const [visible, setVisible] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [reference, setReference] = useState<ReferenceImage | null>(null);
  const [model, setModel] = useState<CoverModel>(imageModelTasks.coverImage.default);

  const { mutation, isLoading, imageUrl } = useChallengeCoverGenerator();

  const onPickReference = async (file: RcFile) => {
    if (file.size / 1024 / 1024 > MAX_REFERENCE_MB) {
      message.error(`Obrazek referencyjny może mieć maksymalnie ${MAX_REFERENCE_MB}MB`);
      return Upload.LIST_IGNORE;
    }
    try {
      const data = await fileToBase64(file);
      setReference({ data, mediaType: file.type || "image/png", fileName: file.name });
    } catch (error) {
      message.error(getErrorMessage(error));
    }
    return false; // handled manually, no upload request
  };

  return (
    <Popover
      open={visible}
      onOpenChange={setVisible}
      content={
        <Space direction="vertical" style={{ width: 460 }}>
          <Input.TextArea
            value={prompt}
            placeholder="Wpisz dodatkowo instrukcje dla AI (opcjonalnie)"
            onChange={(e) => setPrompt(e.target.value || "")}
          />

          <Space.Compact block>
            <Upload accept="image/*" maxCount={1} showUploadList={false} beforeUpload={onPickReference}>
              <Button icon={<PictureOutlined />}>
                {reference ? `Referencja: ${reference.fileName}` : "Obrazek referencyjny (opcjonalnie)"}
              </Button>
            </Upload>
            {reference ? (
              <Button icon={<DeleteOutlined />} onClick={() => setReference(null)} title="Usuń referencję" />
            ) : null}
          </Space.Compact>
          {reference ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Obrazek zostanie przekazany do modelu jako referencja stylu/kompozycji.
            </Typography.Text>
          ) : null}

          <Select
            value={model}
            onChange={setModel}
            style={{ width: "100%" }}
            options={getImageModelSelectOptions("coverImage")}
          />

          <div className="flex justify-end gap-3">
            {imageUrl ? (
              <Button
                type="primary"
                onClick={() => {
                  setCoverUrl(imageUrl);
                  setVisible(false);
                }}
              >
                Ustaw jako okładkę
              </Button>
            ) : null}
            <Button
              type={imageUrl ? "default" : "primary"}
              loading={isLoading}
              disabled={!challengeContext.name}
              onClick={() => {
                mutation.mutate({
                  challengeId,
                  prompt,
                  model,
                  challengeContext,
                  referenceImage: reference,
                });
              }}
            >
              {imageUrl ? "Generuj na nowo" : "Generuj"}
            </Button>
          </div>

          <div className="my-4">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-y-2">
                {/* 16:9 — the same landscape frame the app renders covers in */}
                <Skeleton.Image className="rounded-md" active style={{ width: 416, height: 234 }} />
                <Typography.Text type="secondary" className="text-center text-sm">
                  Generowanie może zająć nawet około 1 minutę...
                </Typography.Text>
              </div>
            ) : imageUrl ? (
              <div className="flex items-center justify-center">
                <Image width={416} height={234} style={{ objectFit: "cover", borderRadius: 8 }} src={imageUrl} />
              </div>
            ) : null}
          </div>
        </Space>
      }
      title="Generuj okładkę wyzwania"
      trigger="click"
      placement="bottom"
    >
      <Button icon={<ExperimentOutlined />}>Wygeneruj okładkę</Button>
    </Popover>
  );
};

export default ChallengeCoverAiGenerator;
